import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { CONTACT } from "@/config/content";
import { ContactForm } from "./ContactForm";

const EASE = [0.16, 1, 0.3, 1];

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

export default function ContactSection() {
  const whatsappHref = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(CONTACT.whatsappText)}`;

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="px-6 lg:px-12 py-24 lg:py-40 border-t border-black/5"
    >
      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "100px" }}
        >
          <p className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-8">Projekt starten</p>
          <h2
            data-testid="contact-headline"
            className="font-display uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-10"
          >
            Du hast eine Idee?{" "}
            <span className="font-editorial italic normal-case tracking-normal text-gold">Lass uns daraus etwas Echtes machen.</span>
          </h2>
          <p className="text-base leading-relaxed text-zinc-500 max-w-md mb-12">
            Schreib mir unkompliziert, worum es geht — Website, Clothing Design oder
            deine eigene Brand. Ich melde mich schnell zurück und wir besprechen
            alles Weitere persönlich.
          </p>

          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click")}
            data-testid="whatsapp-button"
            className="group relative overflow-hidden inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-ink text-paper px-8 py-5 text-xs uppercase tracking-[0.25em] mb-10"
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative flex items-center gap-3 group-hover:text-ink transition-colors duration-500">
              <MessageCircle size={18} />
              Per WhatsApp anfragen
            </span>
          </a>

          <div className="space-y-6">
            <a href={`mailto:${CONTACT.email}`} onClick={() => trackEvent("email_click")} data-testid="contact-email-link" className="group flex items-center gap-5">
              <span className="w-11 h-11 border border-black/10 flex items-center justify-center text-ink group-hover:bg-ink group-hover:text-paper transition-colors duration-500">
                <Mail size={16} />
              </span>
              <span className="text-base group-hover:text-gold transition-colors duration-300">{CONTACT.email}</span>
            </a>
            <a href={`tel:${CONTACT.phoneHref}`} onClick={() => trackEvent("anruf_click")} data-testid="contact-phone-link" className="group flex items-center gap-5">
              <span className="w-11 h-11 border border-black/10 flex items-center justify-center text-ink group-hover:bg-ink group-hover:text-paper transition-colors duration-500">
                <Phone size={16} />
              </span>
              <span className="text-base group-hover:text-gold transition-colors duration-300">{CONTACT.phoneDisplay}</span>
            </a>
          </div>
        </motion.div>

        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "100px" }}>
          <ContactForm className="border border-black/10 p-8 lg:p-12 bg-white" />
        </motion.div>
      </div>
    </section>
  );
}
